/**
 * GA4 전환 측정 헬퍼 (기관회원 포털 문의/가입 신청 완료 전용)
 *
 * - 기본값은 완전 OFF: 아래 3개 빌드 env 가 모두 설정되어야만 동작합니다.
 *   REACT_APP_GA_MEASUREMENT_ID          : 관리자/회원 포털 전용 GA4 웹 스트림 ID (G-XXXX). 공개 사이트 스트림 ID 사용 금지
 *   REACT_APP_GA_ALLOWED_HOST            : 배포 호스트명 (정확히 일치해야 함, 예: 실제 운영 도메인)
 *   REACT_APP_GA_ENHANCED_MEASUREMENT_OFF: 'true' (GA 콘솔에서 해당 스트림의 향상된 측정 OFF 확인 후에만 설정)
 * - 페이지 로드 시 gtag 를 불러오지 않습니다. 허용된 전환(문의 완료/가입 신청 완료) 시점에만 지연 로드합니다.
 * - send_page_view:false, Google 신호/광고 개인화 OFF, user_id 미설정.
 * - 전송 파라미터는 고정값(send_to, inquiry_type, app_area)과 정적 page_location/page_title 만 사용합니다.
 *   (게시물 ID, 쿼리스트링, 제목/본문, 이메일, 파일명 등은 절대 전송하지 않음)
 */

export const INQUIRY_COMPLETE_EVENT = 'inquiry_complete';
export const INQUIRY_BOARD_TYPE = 'question';
export const INQUIRY_ROUTE = '/question/write';
export const MEMBERSHIP_APPLY_EVENT = 'membership_apply_complete';
export const MEMBERSHIP_APPLY_ROUTE = '/membershipComplete';
// 이 포털의 'user'는 지역 기관 담당자(기관회원) 권한이며 일반회원이 아닙니다.
export const MEMBER_USER_TYPE = 'user';

// 공개(사용자) 사이트 스트림. 관리자/회원 포털에서는 절대 사용하지 않음
const BLOCKED_MEASUREMENT_IDS = ['G-PHD602WQ5J'];
// document.title 자동 수집 대신 고정 타이틀 사용 (타이틀 변경/자유 텍스트 유입 방지)
export const SAFE_PAGE_TITLE = 'DREAMS 회원 포털';
const MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]{4,20}$/;
const HOST_PATTERN = /^[a-z0-9.-]+(:\d{1,5})?$/;

// CRA 는 process.env.REACT_APP_* 리터럴을 빌드 시 치환하므로 명시적으로 참조
const readBuildEnv = () => ({
  REACT_APP_GA_MEASUREMENT_ID: process.env.REACT_APP_GA_MEASUREMENT_ID,
  REACT_APP_GA_ALLOWED_HOST: process.env.REACT_APP_GA_ALLOWED_HOST,
  REACT_APP_GA_ENHANCED_MEASUREMENT_OFF: process.env.REACT_APP_GA_ENHANCED_MEASUREMENT_OFF
});

const getWindow = () => (typeof window === 'undefined' ? null : window);

/**
 * GA 설정 확인 (설정 미비 시 disabled)
 * @param {object} env - 빌드 env (테스트용 주입 가능)
 * @param {Location} loc - window.location (테스트용 주입 가능)
 * @returns {{ enabled: boolean, reason: string, measurementId?: string, origin?: string }}
 */
export const getAnalyticsConfig = (env = readBuildEnv(), loc = getWindow()?.location) => {
  const measurementId = String(env?.REACT_APP_GA_MEASUREMENT_ID || '').trim();
  const allowedHost = String(env?.REACT_APP_GA_ALLOWED_HOST || '')
    .trim()
    .toLowerCase();
  const emOffConfirmed = String(env?.REACT_APP_GA_ENHANCED_MEASUREMENT_OFF || '').trim() === 'true';

  if (!measurementId) return { enabled: false, reason: 'missing_measurement_id' };
  if (!MEASUREMENT_ID_PATTERN.test(measurementId)) return { enabled: false, reason: 'invalid_measurement_id' };
  if (BLOCKED_MEASUREMENT_IDS.includes(measurementId)) return { enabled: false, reason: 'blocked_measurement_id' };
  if (!allowedHost || !HOST_PATTERN.test(allowedHost)) return { enabled: false, reason: 'missing_allowed_host' };
  if (!emOffConfirmed) return { enabled: false, reason: 'enhanced_measurement_not_confirmed_off' };
  if (!loc || String(loc.host || '').toLowerCase() !== allowedHost) return { enabled: false, reason: 'host_not_allowed' };
  if (loc.protocol !== 'https:') return { enabled: false, reason: 'insecure_protocol' };

  return { enabled: true, reason: 'ok', measurementId, origin: `https://${allowedHost}` };
};

// referrer 는 origin 만 남김 (경로/쿼리 제거)
const safeReferrer = () => {
  const w = getWindow();
  try {
    const ref = w?.document?.referrer;
    return ref ? `${new URL(ref).origin}/` : '';
  } catch (e) {
    return '';
  }
};

let loadedMeasurementId = null;

/**
 * gtag 지연 로드 (중복 로드 방지). 자동 page_view 및 광고 기능 비활성화
 */
const ensureGtag = ({ measurementId, origin }, route) => {
  const w = getWindow();
  if (!w) return null;

  if (loadedMeasurementId !== measurementId) {
    w.dataLayer = w.dataLayer || [];
    if (typeof w.gtag !== 'function') {
      w.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        w.dataLayer.push(arguments);
      };
    }

    w.gtag('consent', 'default', { ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
    w.gtag('js', new Date());
    w.gtag('config', measurementId, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      page_location: `${origin}${route}`,
      page_title: SAFE_PAGE_TITLE,
      page_referrer: safeReferrer()
    });

    const script = w.document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    script.setAttribute('data-ga-conversion-only', 'true');
    w.document.head.appendChild(script);

    loadedMeasurementId = measurementId;
  }
  return w.gtag;
};

// 각 전환의 정적 경로와 고정값만 전달. 신청서/응답 본문은 매개변수로 받지 않습니다.
const trackPortalConversion = (eventName, route, params, env, loc) => {
  const config = getAnalyticsConfig(env, loc);
  if (!config.enabled) return false;

  const gtag = ensureGtag(config, route);
  if (!gtag) return false;

  gtag('event', eventName, {
    ...params,
    send_to: config.measurementId,
    app_area: 'member_portal',
    page_location: `${config.origin}${route}`,
    page_title: SAFE_PAGE_TITLE
  });
  return true;
};

export const trackInquiryComplete = (env, loc) =>
  trackPortalConversion(INQUIRY_COMPLETE_EVENT, INQUIRY_ROUTE, { inquiry_type: 'question' }, env, loc);

// 가입 신청 접수 성공이며, 협의회의 가입 승인을 의미하지 않습니다.
export const trackMembershipApplicationComplete = (env, loc) =>
  trackPortalConversion(MEMBERSHIP_APPLY_EVENT, MEMBERSHIP_APPLY_ROUTE, {}, env, loc);

/**
 * 문의 신규 등록 성공 여부 판단
 * - 기관회원(user) 권한 + 문의하기(question) 게시판 + 신규 등록(create) + 응답 status 201 인 경우에만 true
 * - 수정(edit), 관리자/마스터 작성, 다른 게시판, 실패 응답은 모두 false
 */
export const isInquiryCreateSuccess = ({ boardType, submittedBoardType, requestType, userType, response }) =>
  boardType === INQUIRY_BOARD_TYPE &&
  submittedBoardType === INQUIRY_BOARD_TYPE &&
  requestType === 'create' &&
  userType === MEMBER_USER_TYPE &&
  response?.data?.status === 201;

/**
 * BoardWrite 저장 결과 → 문의 완료 전환 (폼 인스턴스당 1회, 분석 오류는 화면 흐름에 영향 없음)
 * @param {object} params
 * @param {{ current: boolean }} params.guard - 중복 전송 방지용 ref
 * @returns {boolean} 전송 여부
 */
export const reportInquiryCreateResult = ({ guard, env, loc, ...result }) => {
  try {
    if (!guard || guard.current) return false;
    if (!isInquiryCreateSuccess(result)) return false;
    guard.current = true;
    return trackInquiryComplete(env, loc);
  } catch (e) {
    return false;
  }
};

// /user/register의 응답 body status 201만 인정. 이메일 인증/완료 화면 조회/승인 처리는 대상이 아닙니다.
export const reportMembershipApplicationResult = ({ guard, response, env, loc }) => {
  try {
    if (!guard || guard.current || response?.data?.status !== 201) return false;
    guard.current = true;
    return trackMembershipApplicationComplete(env, loc);
  } catch (e) {
    return false;
  }
};

// 테스트 전용
export const __resetAnalyticsForTest = () => {
  loadedMeasurementId = null;
};
